import React, { useMemo } from 'react';
import { BeveledPanel } from '../FabProcessTool/BeveledPanel';
import {
  getBlueIndustrialGlass,
  getDarkChamberGlass,
} from '../FabProcessTool/equipmentMaterials';
import { shouldRenderAmhsPhysicalGlass } from '../../rendering';
import type { FabToolDetail } from '../FabProcessTool/types';
import type { Awp300Layout } from './awp300Layout';

export type Awp300GlassProps = {
  layout: Awp300Layout;
  detail?: FabToolDetail;
  opacity?: number;
};

/** Front / side cyan-blue glass, back smoked glass, thin blue roof. */
export const Awp300Glass: React.FC<Awp300GlassProps> = ({
  layout,
  detail = 'high',
  opacity = 1,
}) => {
  const physical = shouldRenderAmhsPhysicalGlass(detail);
  const glassQuality = physical ? 'high' : 'low';
  const sideGlass = useMemo(
    () => getBlueIndustrialGlass(opacity, glassQuality),
    [opacity, glassQuality]
  );
  const backGlass = useMemo(
    () => getDarkChamberGlass(opacity, glassQuality),
    [opacity, glassQuality]
  );
  const topGlass = sideGlass;

  const { width, depth, hx, hz, glassH, glassY, pillarTop, frameT } = layout;
  const panelD = Math.max(frameT * 0.3, depth * 0.026);
  const topThin = Math.max(frameT * 0.2, 0.018);
  const r = Math.min(width, depth) * 0.01;

  return (
    <group name="Awp300Glass">
      {([-1, 1] as const).map((side) => (
        <BeveledPanel
          key={`side-${side}`}
          size={[panelD, glassH, depth * 0.9]}
          material={sideGlass}
          position={[side * (hx + side * panelD * 0.5), glassY, 0]}
          radius={r * 0.15}
          smoothness={3}
        />
      ))}
      <BeveledPanel
        size={[width * 0.86, glassH * 0.98, panelD]}
        material={backGlass}
        position={[0, glassY, hz - panelD * 0.45]}
        radius={r * 0.2}
        smoothness={3}
      />
      <BeveledPanel
        size={[width * 0.88, topThin, depth * 0.86]}
        material={topGlass}
        position={[0, pillarTop - topThin * 0.55, 0]}
        radius={r * 0.12}
        smoothness={3}
      />
    </group>
  );
};

export default Awp300Glass;
